import React, { useContext, useMemo } from 'react'
import { type RefetchFn } from 'domains/retailer-collections/utils/types'
import { ErrorPage } from 'gin-and-tonic/containers/error-page/ErrorPage'
import { LoadingPage } from 'gin-and-tonic/containers/loading-page/LoadingPage'
import { useDomainMessages } from 'domains/retailer-collections/utils/domain/intl'
import {
  type RetailerCollectionsRetailerConfig,
  useGetRetailerConfig,
} from 'domains/retailer-collections/utils/useGetRetailerConfig.hooks'

export interface IRetailerCollectionsContext extends RetailerCollectionsRetailerConfig {
  refetchConfig: RefetchFn
}

export const retailerCollectionsContext = React.createContext<IRetailerCollectionsContext>(null)

export const useRetailerCollectionsContext = () => useContext(retailerCollectionsContext)

export const RetailerCollectionsContext: React.FunctionComponent = ({ children }) => {
  const { config, error, refetch, loading } = useGetRetailerConfig()

  const messages = useDomainMessages({
    notEnabledForRetailer: 'retailerCollectionsDomain.featureNotEnabled',
    failedLoadingConfig: 'retailerCollectionsDomain.failedLoadingConfig',
  })

  const contextValue = useMemo(
    () => ({
      ...config,
      refetchConfig: refetch,
    }),
    [config, refetch]
  )

  if (loading) return <LoadingPage />

  if (error) return <ErrorPage action={() => refetch()}>{messages.failedLoadingConfig}</ErrorPage>

  // If no available Store Configurations, show error page, retailer not supported
  if (!config?.storeConfigurations || config?.storeConfigurations?.length === 0)
    return <ErrorPage>{messages.notEnabledForRetailer}</ErrorPage>

  return (
    <retailerCollectionsContext.Provider value={contextValue}>
      {children}
    </retailerCollectionsContext.Provider>
  )
}
